import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Inject,
  NgZone,
  OnDestroy,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { TranslatePipe } from '@ngx-translate/core';
import { Model as SurveyModel } from 'survey-core';
import { VisualizationPanel } from 'survey-analytics';
import 'survey-analytics/survey.analytics.min.css';

import { UiAtomButtonComponent } from '@shared/components/atoms/ui-atom-button/ui-atom-button.component';
import { UiAtomTextComponent } from '@shared/components/atoms/ui-atom-text/ui-atom-text.component';

export interface FormDashboardDialogData {
  formName: string;
  schema: Record<string, unknown>;
  /** Array de answers (cada uno es un response.answers plano). */
  responses: Array<Record<string, unknown>>;
  /** Etiqueta opcional para el subtítulo (p.ej. "Global", "Country: ES"). */
  scopeLabel?: string;
}

@Component({
  selector: 'ui-molecule-dialog-form-dashboard',
  standalone: true,
  imports: [CommonModule, MatIconModule, TranslatePipe, UiAtomButtonComponent, UiAtomTextComponent],
  templateUrl: './ui-molecule-dialog-form-dashboard.component.html',
  styleUrl: './ui-molecule-dialog-form-dashboard.component.scss',
  encapsulation: ViewEncapsulation.None,
})
export class FormDashboardDialogComponent implements AfterViewInit, OnDestroy {
  @ViewChild('vizContainer', { static: true })
  private vizContainer!: ElementRef<HTMLDivElement>;

  protected isExpanded = false;
  protected isEmpty = false;

  private vizPanel: VisualizationPanel | null = null;
  private windowResizeHandler: (() => void) | null = null;
  private windowResizeDebounce: ReturnType<typeof setTimeout> | null = null;

  constructor(
    public dialogRef: MatDialogRef<FormDashboardDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: FormDashboardDialogData,
    private cdr: ChangeDetectorRef,
    private zone: NgZone,
  ) {}

  ngAfterViewInit(): void {
    const responses = this.data.responses ?? [];
    if (responses.length === 0) {
      this.isEmpty = true;
      this.cdr.detectChanges();
      return;
    }

    const survey = new SurveyModel(this.data.schema ?? {});
    const questions = survey.getAllQuestions();

    if (questions.length === 0) {
      this.isEmpty = true;
      this.cdr.detectChanges();
      return;
    }

    this.vizPanel = new VisualizationPanel(questions, responses, {
      allowHideQuestions: false,
    });

    // Renderizar al terminar la animación de apertura del diálogo: antes
    // de ese momento el contenedor aún no tiene el ancho final y Plotly
    // mediría un width obsoleto, dejando el gráfico estrecho.
    this.dialogRef.afterOpened().subscribe(() => {
      this.vizPanel?.render(this.vizContainer.nativeElement);
      this.attachWindowResizeListener();
      this.cdr.detectChanges();
    });
  }

  ngOnDestroy(): void {
    if (this.windowResizeDebounce !== null) {
      clearTimeout(this.windowResizeDebounce);
      this.windowResizeDebounce = null;
    }
    if (this.windowResizeHandler) {
      window.removeEventListener('resize', this.windowResizeHandler);
      this.windowResizeHandler = null;
    }
    if (this.vizPanel) {
      try {
        this.vizPanel.destroy();
      } catch {
        // SurveyJS analytics puede lanzar al limpiar si el DOM ya está desmontado; ignorar.
      }
      this.vizPanel = null;
    }
  }

  toggleExpanded(): void {
    this.isExpanded = !this.isExpanded;

    if (this.isExpanded) {
      this.dialogRef.updateSize('100vw', '100vh');
      this.dialogRef.addPanelClass('dialog-expanded');
      this.dialogRef.removePanelClass('dialog-safe-area');
    } else {
      this.dialogRef.updateSize('80vw', '90vh');
      this.dialogRef.removePanelClass('dialog-expanded');
      this.dialogRef.addPanelClass('dialog-safe-area');
    }

    // Esperar a que el panel del diálogo aplique el nuevo tamaño antes de
    // pedirle a Plotly que recalcule. 220 ms = un poco más que la animación
    // por defecto de MatDialog (≈ 150 ms).
    setTimeout(() => this.vizPanel?.refresh(), 220);
  }

  /**
   * Escucha resize de la ventana (no del contenedor) para que un cambio
   * de tamaño del navegador refleje en los gráficos. Se debounce a 200 ms
   * y se ejecuta fuera de Angular para no disparar change detection.
   *
   * Nota: no usamos `ResizeObserver` sobre el contenedor del visualizer
   * porque cada `refresh()` provoca un re-render que reflowa internamente
   * y dispararía el observer en bucle, causando flicker visual.
   */
  private attachWindowResizeListener(): void {
    this.zone.runOutsideAngular(() => {
      this.windowResizeHandler = () => {
        if (this.windowResizeDebounce !== null) clearTimeout(this.windowResizeDebounce);
        this.windowResizeDebounce = setTimeout(() => {
          this.vizPanel?.refresh();
        }, 200);
      };
      window.addEventListener('resize', this.windowResizeHandler);
    });
  }

  onClose(): void {
    this.dialogRef.close();
  }
}
